import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/shared/Navbar';
import usePricing from '../utils/usePricing';
import useSettings from '../utils/useSettings';
import SEO from '../components/SEO';
import StoryPreviewWidget from '../components/StoryPreviewWidget';
import LiveActivityBar from '../components/LiveActivityBar';
import StoryOfTheDay from '../components/StoryOfTheDay';
import ExitIntentPopup from '../components/ExitIntentPopup';
import AppStoreBadges from '../components/AppStoreBadges';
import useSocialProof, { niceCount } from '../utils/useSocialProof';
import { trackLandingCta, trackLandingDemoPlay } from '../utils/analytics';
import SchemaMarkup, {
  buildOrganizationSchema,
  buildSoftwareSchema,
  buildWebSiteSchema,
  buildFaqSchema,
} from '../components/SchemaMarkup';

const HOMEPAGE_FAQS = [
  { q: 'Is Dreamy Bedtime free?', a: 'Yes! You get 5 free personalised bedtime stories every month with no credit card required. Paid plans unlock unlimited stories and extra features.' },
  { q: 'How long does it take to generate a story?', a: 'Stories are generated in about 15 seconds. Add a child\'s name, age, and interests and the story is ready almost instantly.' },
  { q: 'What languages are supported?', a: 'Dreamy Bedtime supports 11 languages: English, Spanish, French, Hindi, German, Portuguese, Arabic, Chinese, Japanese, Italian, and Gujarati.' },
  { q: 'Can I add my child\'s name to the story?', a: 'Absolutely — every story is personalised with your child\'s name, age, and favourite interests so the main character is always them.' },
  { q: 'Are the stories safe for young children?', a: 'Yes. All stories are age-appropriate, reviewed, and designed to promote positive values like kindness, courage, and friendship.' },
  { q: 'Can I listen to the stories?', a: 'Yes. Dreamy Bedtime includes natural narration so stories can be read aloud to your child, with voice cloning available on premium plans.' },
  { q: 'Do stories come with illustrations?', a: 'Yes. Each story includes beautiful AI-generated illustrations that match the story\'s theme and characters.' },
  { q: 'Can I cancel my subscription anytime?', a: 'Yes. You can cancel your subscription at any time from your account settings with no cancellation fees.' },
];

const SAMPLE_STORY_TEXT = `Emma and the Brave Little Dragon. Once upon a time, in a land where stars could talk, a curious girl named Emma discovered a tiny dragon afraid of the dark. She held out her hand and whispered, "I'll show you that the night is full of wonders." Together they flew through a sky of glowing constellations, each star telling a gentle story of kindness. By the time they landed back on the soft meadow, the little dragon was no longer afraid — because he had a friend beside him. And Emma learned that the bravest thing of all is choosing to be kind. Goodnight, Emma. Goodnight, little dragon.`;

const LandingPage = () => {
  const { pricing } = usePricing();
  const { settings } = useSettings();
  const socialProof = useSocialProof();
  const familiesLabel = socialProof ? niceCount(socialProof.familiesTotal) : null;
  const [isPlaying, setIsPlaying] = useState(false);
  const uttRef = useRef(null);
  const heroRef = useRef(null);

  // Cancel speech on page refresh / navigation away
  React.useEffect(() => {
    const stop = () => window.speechSynthesis?.cancel();
    window.addEventListener('beforeunload', stop);
    return () => {
      stop();
      window.removeEventListener('beforeunload', stop);
    };
  }, []);

  // ── Hero parallax — layers drift with the pointer for a 3D depth feel ───────
  // Desktop pointers only; phones get the gentle CSS float instead. Fully off
  // under prefers-reduced-motion. Uses rAF so it stays smooth on every machine.
  React.useEffect(() => {
    const el = heroRef.current;
    if (!el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    if (!window.matchMedia('(hover: hover)').matches) return undefined;

    const layers = el.querySelectorAll('[data-depth]');
    if (!layers.length) return undefined;
    let raf = 0;

    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        layers.forEach((l) => {
          const d = parseFloat(l.getAttribute('data-depth')) || 0;
          l.style.transform = `translate3d(${(-nx * d * 36).toFixed(1)}px, ${(-ny * d * 36).toFixed(1)}px, 0)`;
        });
      });
    };
    const reset = () => { cancelAnimationFrame(raf); layers.forEach((l) => { l.style.transform = 'translate3d(0,0,0)'; }); };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', reset);
    return () => { cancelAnimationFrame(raf); el.removeEventListener('mousemove', onMove); el.removeEventListener('mouseleave', reset); };
  }, []);

  const handleListen = () => {
    if (!window.speechSynthesis) return;

    if (isPlaying) {
      window.speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }

    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(SAMPLE_STORY_TEXT);
    utt.rate = 0.88;
    utt.pitch = 1.05;

    // Pick a warm English voice if available
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v =>
      v.lang.startsWith('en') && /samantha|karen|victoria|moira|fiona|daniel|google uk|google us/i.test(v.name)
    ) || voices.find(v => v.lang.startsWith('en'));
    if (preferred) utt.voice = preferred;

    utt.onend = () => setIsPlaying(false);
    utt.onerror = () => setIsPlaying(false);
    uttRef.current = utt;
    window.speechSynthesis.speak(utt);
    setIsPlaying(true);
    trackLandingDemoPlay();
  };
  const landingStats    = settings.landingStats;
  const testimonials    = settings.testimonials;
  const landingFeatures = settings.landingFeatures;
  const flags = settings.featureFlags || {};

  return (
  <div style={{overflowX:'hidden'}}>
    <SEO
      title="Magical Personalised Bedtime Stories for Kids"
      description="Generate unique, personalised bedtime stories in seconds — starring your child, their favourite interests, and meaningful lessons. Free to start. No credit card required."
      canonical="/"
    />
    <SchemaMarkup schema={[
      buildOrganizationSchema(),
      buildSoftwareSchema(),
      buildWebSiteSchema(),
      buildFaqSchema(HOMEPAGE_FAQS),
    ]} />
    <Navbar />
    <ExitIntentPopup />

    {/* ── HERO ─────────────────────────────────────────────── */}
    <section ref={heroRef} className="bg-dream text-white" style={{padding:'80px 0 60px', position:'relative', overflow:'hidden'}}>
      {/* Floating parallax depth layers — purely decorative, click-through */}
      <div aria-hidden="true" style={{position:'absolute', inset:0, zIndex:0, pointerEvents:'none', overflow:'hidden'}}>
        <div data-depth="0.5" className="dream-layer dream-float dream-glow" style={{position:'absolute', top:'9%', right:'9%', width:'clamp(70px,9vw,130px)', height:'clamp(70px,9vw,130px)', borderRadius:'50%', background:'radial-gradient(circle at 35% 32%, #fff7d6, #f9c74f 62%, rgba(249,199,79,0))'}} />
        <div data-depth="1.25" className="dream-layer" style={{position:'absolute', top:'16%', left:'4%', width:'200px', height:'200px', borderRadius:'50%', background:'radial-gradient(circle, rgba(155,111,232,0.40), rgba(155,111,232,0))', filter:'blur(10px)'}} />
        <div data-depth="0.9" className="dream-layer" style={{position:'absolute', bottom:'8%', right:'26%', width:'150px', height:'150px', borderRadius:'50%', background:'radial-gradient(circle, rgba(99,102,241,0.34), rgba(99,102,241,0))', filter:'blur(8px)'}} />
        <div data-depth="1.6" className="dream-layer" style={{position:'absolute', bottom:'18%', left:'22%', width:'90px', height:'90px', borderRadius:'50%', background:'radial-gradient(circle, rgba(244,114,182,0.30), rgba(244,114,182,0))', filter:'blur(6px)'}} />
      </div>
      <div className="container" style={{position:'relative', zIndex:1}}>
        <div className="row align-items-center g-5">
          <div className="col-lg-6">
            <span className="badge bg-warning text-dark mb-3 px-3 py-2 rounded-pill" style={{fontSize:'13px'}}>
              ✨ Smart · Personalised · Magical
            </span>
            <h1 className="fw-bold mb-4" style={{fontFamily:"'Fredoka One',cursive", lineHeight:1.15, fontSize:'clamp(36px,5vw,58px)'}}>
              Bedtime stories your child will <span style={{color:'#f9c74f'}}>never forget</span>
            </h1>
            <p className="mb-4" style={{fontSize:'18px', color:'#c9a8ff', lineHeight:1.7}}>
              Generate a unique, fully personalised bedtime story in seconds — starring <strong style={{color:'#fff'}}>your child</strong>, their favourite interests, and lessons that actually matter. Every night. Forever.
            </p>
            <div className="d-flex flex-wrap gap-3 mb-4">
              <Link to="/register" className="btn btn-warning btn-lg px-5 rounded-pill fw-bold" style={{fontSize:'17px'}} onClick={() => trackLandingCta('Start Free', '/register')}>
                Start Free — 5 Stories/Month
              </Link>
              <Link to="/pricing" className="btn btn-outline-light btn-lg px-4 rounded-pill" onClick={() => trackLandingCta('See Pricing', '/pricing')}>
                See Pricing
              </Link>
            </div>
            <AppStoreBadges placement="hero" height={44} className="mb-4" />
            <div className="d-flex flex-wrap gap-3 mb-4">
              {['No credit card required','Cancel anytime','Stories in 15 seconds'].map(t => (
                <span key={t} style={{color:'#c9a8ff', fontSize:'13px'}}>
                  <i className="bi bi-check-circle-fill me-1" style={{color:'#4ade80'}}></i>{t}
                </span>
              ))}
            </div>
            {/* Real family count from the DB — hidden until it crosses the
                first marketing tier so we never show tiny/fake numbers */}
            {familiesLabel && (
              <div className="d-flex align-items-center gap-2">
                <div className="d-flex">
                  {['#f472b6','#818cf8','#34d399','#fb923c','#60a5fa'].map((color, i) => (
                    <div key={i} style={{
                      width:'28px', height:'28px', borderRadius:'50%',
                      background: color,
                      border: '2px solid rgba(255,255,255,0.4)',
                      marginLeft: i === 0 ? 0 : '-8px',
                      fontSize: '13px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>👤</div>
                  ))}
                </div>
                <span style={{color:'#c9a8ff', fontSize:'13px'}}>
                  <span style={{color:'#f9c74f', letterSpacing:'1px'}}>★★★★★</span>
                  {' '}Loved by <strong style={{color:'#fff'}}>{familiesLabel} families</strong>
                </span>
              </div>
            )}
          </div>

          <div className="col-lg-6" data-depth="0.18">
            <div className="mx-auto float-anim" style={{maxWidth:'400px'}}>
              <div className="card border-0 shadow-lg p-4" style={{borderRadius:'24px', background:'rgba(255,255,255,0.1)', backdropFilter:'blur(16px)', border:'1px solid rgba(255,255,255,0.2)'}}>
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{width:'36px',height:'36px',background:'#f9c74f',fontSize:'18px'}}>🧒</div>
                  <div>
                    <div style={{color:'#fff',fontWeight:700,fontSize:'14px'}}>Emma, age 5</div>
                    <div style={{color:'#c9a8ff',fontSize:'12px'}}>Loves: Dragons, Magic, Space</div>
                  </div>
                  <span className="ms-auto badge rounded-pill" style={{background:'#4ade80',color:'#1a4a2a',fontSize:'11px'}}>✓ Generated</span>
                </div>
                <div className="p-3 rounded-3 mb-3" style={{background:'var(--card-bg)'}}>
                  <div className="fw-bold mb-2" style={{color:'var(--text-main)',fontFamily:"'Fredoka One',cursive",fontSize:'17px'}}>
                    Emma and the Brave Little Dragon
                  </div>
                  <p style={{color:'var(--text-muted2)',fontSize:'13px',lineHeight:1.7,margin:0}}>
                    Once upon a time, in a land where stars could talk, a curious girl named Emma discovered a tiny dragon afraid of the dark. She held out her hand and whispered, "I'll show you that the night is full of wonders..."
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span style={{color:'rgba(255,255,255,0.6)',fontSize:'12px'}}>📖 4 min · ✨ Kindness theme</span>
                  <button className="btn btn-warning btn-sm rounded-pill ms-auto px-3" style={{fontSize:'12px'}} onClick={handleListen}>
                    {isPlaying ? '⏹ Stop' : '🔊 Listen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── LIVE ACTIVITY (real DB stats, threshold-gated) ────── */}
    <LiveActivityBar />

    {/* ── SOCIAL PROOF BAR ─────────────────────────────────── */}
    <section className="py-4 bg-white border-bottom">
      <div className="container">
        <div className="row text-center g-3">
          {landingStats.map(({ stat, label }) => (
            <div className="col-6 col-md-3" key={label}>
              <div className="fw-bold" style={{color:'#6f42c1',fontFamily:"'Fredoka One',cursive",fontSize:'28px'}}>{stat}</div>
              <div className="text-muted small">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── INTERACTIVE STORY PREVIEW ────────────────────────── */}
    <StoryPreviewWidget />

    {/* ── HOW IT WORKS ─────────────────────────────────────── */}
    <section className="py-5 bg-white">
      <div className="container py-3">
        <div className="text-center mb-5">
          <span className="section-badge">How It Works</span>
          <h2 className="fw-bold" style={{fontFamily:"'Fredoka One',cursive",color:'var(--text-main)',fontSize:'clamp(28px,4vw,40px)'}}>
            Magical bedtime stories in 3 steps
          </h2>
          <p className="text-muted" style={{maxWidth:'520px',margin:'0 auto',fontSize:'16px'}}>
            No writing skills needed. No prep. Just one tap and a story that feels like it was made by a professional author — because it was, kind of.
          </p>
        </div>
        <div className="row g-4 justify-content-center">
          {[
            { step:'1', icon:'👶', title:'Create your child\'s profile', color:'#f0e8ff', desc:'Add their name, age, interests, and favourite characters. It only takes 2 minutes and makes every story feel custom-made.' },
            { step:'2', icon:'🪄', title:'Pick tonight\'s options', color:'#e8f4ff', desc:'Choose a theme — kindness, bravery, Halloween, new sibling. Or just hit Generate and let us decide. Either way, it\'s perfect.' },
            { step:'3', icon:'🌙', title:'Read, listen & drift off', color:'#e8fff0', desc:'Your story is ready in under 15 seconds. Read it yourself, or tap Smart Narration for a warm voice that reads it aloud while you snuggle.' },
          ].map((s, i) => (
            <div className="col-md-4" key={s.step}>
              <div className="card border-0 shadow-sm h-100 p-4 text-center" style={{borderRadius:'20px'}}>
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center fw-bold text-white mb-3"
                  style={{width:'44px',height:'44px',background:'#6f42c1',fontSize:'18px',margin:'0 auto'}}>
                  {s.step}
                </div>
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{width:'72px',height:'72px',background:s.color,fontSize:'32px',margin:'0 auto'}}>
                  {s.icon}
                </div>
                <h5 className="fw-bold mb-2" style={{color:'var(--text-main)'}}>{s.title}</h5>
                <p className="text-muted mb-0" style={{fontSize:'14px',lineHeight:1.7}}>{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── STORY OF THE DAY (community story, rotates daily) ── */}
    <StoryOfTheDay />

    {/* ── ALL FEATURES ─────────────────────────────────────── */}
    <section className="py-5" style={{background:'#f8f5ff'}}>
      <div className="container py-3">
        <div className="text-center mb-5">
          <span className="section-badge">Features</span>
          <h2 className="fw-bold" style={{fontFamily:"'Fredoka One',cursive",color:'var(--text-main)',fontSize:'clamp(28px,4vw,40px)'}}>
            Everything you need for magical bedtimes
          </h2>
          <p className="text-muted" style={{maxWidth:'520px',margin:'0 auto',fontSize:'16px'}}>
            Built for busy parents who still want bedtime to feel special.
          </p>
        </div>
        <div className="row g-4">
          {landingFeatures.map(f => (
            <div className="col-md-6 col-lg-4" key={f.title}>
              <div className="card border-0 shadow-sm h-100 p-4 card-hover" style={{borderRadius:'18px',background:'var(--card-bg)'}}>
                <div className="feature-icon mb-3" style={{background:f.bg}}>
                  {f.icon}
                </div>
                <h6 className="fw-bold mb-2" style={{color:'var(--text-main)',fontSize:'15px'}}>{f.title}</h6>
                <p className="mb-0" style={{color:'var(--text-muted2)',fontSize:'13px',lineHeight:1.7}}>{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── STORY ILLUSTRATION SPOTLIGHT ─────────────────────── */}
    <section className="py-5 bg-white">
      <div className="container py-3">
        <div className="row align-items-center g-5">
          <div className="col-lg-6">
            <span className="section-badge">Magical Illustrations</span>
            <h2 className="fw-bold mb-3" style={{fontFamily:"'Fredoka One',cursive",color:'var(--text-main)',fontSize:'clamp(26px,4vw,38px)'}}>
              Every story gets its own artwork
            </h2>
            <p style={{color:'var(--text-muted2)',fontSize:'16px',lineHeight:1.8,marginBottom:'24px'}}>
              After your story is generated, we create a custom illustration — a unique piece of digital art that captures the key scene. Warm watercolour style, always child-appropriate.
            </p>
            <div className="d-flex flex-column gap-3 mb-4">
              {[
                ['🎨','Vivid, warm watercolour style — unique every time'],
                ['☁️','Stored permanently in the cloud — never expires or disappears'],
                ['💾','Download any illustration to keep or print'],
                ['🔁','Paid plans can regenerate for a different scene'],
              ].map(([icon,text]) => (
                <div key={text} className="d-flex align-items-start gap-3">
                  <span style={{fontSize:'20px',lineHeight:1}}>{icon}</span>
                  <span style={{color:'var(--text-muted2)',fontSize:'14px',lineHeight:1.6}}>{text}</span>
                </div>
              ))}
            </div>
            <Link to="/register" className="btn btn-dream rounded-pill px-4 fw-semibold">
              Try It Free
            </Link>
          </div>
          <div className="col-lg-6">
            <div className="rounded-4 overflow-hidden shadow-lg" style={{background:'linear-gradient(135deg,#f0e8ff,#e8d5ff)',padding:'32px'}}>
              <div className="rounded-3 mb-3" style={{background:'linear-gradient(135deg,#667eea,#764ba2)',height:'220px',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <div className="text-center text-white">
                  <div style={{fontSize:'56px',marginBottom:'8px'}}>🐉</div>
                  <div style={{fontFamily:"'Fredoka One',cursive",fontSize:'18px',opacity:0.9}}>Illustration Preview</div>
                  <div style={{fontSize:'13px',opacity:0.7,marginTop:'4px'}}>Warm watercolour · Pixar-inspired</div>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 p-3 rounded-3" style={{background:'var(--card-bg)'}}>
                <div style={{fontSize:'28px'}}>🌙</div>
                <div>
                  <div className="fw-bold" style={{color:'var(--text-main)',fontSize:'15px'}}>Emma and the Brave Little Dragon</div>
                  <div style={{color:'var(--text-soft)',fontSize:'12px'}}>Illustration generated in ~20 seconds</div>
                </div>
                <button className="btn btn-sm rounded-pill ms-auto" style={{background:'#f0e8ff',color:'#6f42c1',fontSize:'12px',fontWeight:600}}>
                  <i className="bi bi-download me-1"></i>Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── LANGUAGES SPOTLIGHT ──────────────────────────────── */}
    <section className="py-5" style={{background:'#f8f5ff'}}>
      <div className="container py-3">
        <div className="row align-items-center g-5">
          <div className="col-lg-6 order-lg-2">
            <span className="section-badge">Multilingual</span>
            <h2 className="fw-bold mb-3" style={{fontFamily:"'Fredoka One',cursive",color:'var(--text-main)',fontSize:'clamp(26px,4vw,38px)'}}>
              Stories in your family's language
            </h2>
            <p style={{color:'var(--text-muted2)',fontSize:'16px',lineHeight:1.8,marginBottom:'24px'}}>
              Bilingual families are hugely underserved. Dreamy Bedtime generates stories in 11 languages — just select yours before hitting generate. The entire story, including the title and moral lesson, is written in your chosen language.
            </p>
            <div className="d-flex flex-wrap gap-2 mb-4">
              {[['🇬🇧','English'],['🇪🇸','Spanish'],['🇫🇷','French'],['🇮🇳','Hindi'],['🇩🇪','German'],['🇧🇷','Portuguese'],['🇸🇦','Arabic'],['🇨🇳','Chinese'],['🇯🇵','Japanese'],['🇮🇹','Italian']].map(([flag,lang]) => (
                <span key={lang} className="badge rounded-pill px-3 py-2" style={{background:'var(--card-bg)',color:'var(--text-main)',border:'1px solid #e0d0ff',fontSize:'13px',fontWeight:600}}>
                  {flag} {lang}
                </span>
              ))}
            </div>
            <Link to="/register" className="btn btn-dream rounded-pill px-4 fw-semibold">
              Get Started Free
            </Link>
          </div>
          <div className="col-lg-6 order-lg-1">
            <div className="card border-0 shadow-lg p-4" style={{borderRadius:'20px'}}>
              <div className="fw-bold mb-3" style={{color:'var(--text-main)',fontSize:'16px'}}>
                <i className="bi bi-translate me-2" style={{color:'#6f42c1'}}></i>Same story, any language
              </div>
              {[
                ['🇬🇧','Emma and the Brave Dragon','English'],
                ['🇪🇸','Emma y el Dragón Valiente','Spanish'],
                ['🇫🇷','Emma et le Dragon Courageux','French'],
                ['🇮🇳','एम्मा और बहादुर ड्रैगन','Hindi'],
              ].map(([flag,title,lang]) => (
                <div key={lang} className="d-flex align-items-center gap-3 p-2 rounded-3 mb-2" style={{background:'#f8f5ff'}}>
                  <span style={{fontSize:'20px'}}>{flag}</span>
                  <div>
                    <div style={{color:'var(--text-main)',fontWeight:600,fontSize:'14px'}}>{title}</div>
                    <div style={{color:'var(--text-soft)',fontSize:'12px'}}>{lang}</div>
                  </div>
                  <i className="bi bi-check-circle-fill ms-auto" style={{color:'#4ade80'}}></i>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── VOICE CLONING SPOTLIGHT ──────────────────────────── */}
    <section className="py-5 bg-white">
      <div className="container py-3">
        <div className="row align-items-center g-5">
          <div className="col-lg-6">
            <span className="section-badge" style={{background:'#fff7ed', color:'#ea580c'}}>🎙️ Family Plan</span>
            <h2 className="fw-bold mb-3" style={{fontFamily:"'Fredoka One',cursive", color:'var(--text-main)', fontSize:'clamp(26px,4vw,38px)'}}>
              Stories narrated in <span style={{color:'#ea580c'}}>your own voice</span>
            </h2>
            <p style={{color:'var(--text-muted2)', fontSize:'16px', lineHeight:1.8, marginBottom:'24px'}}>
              No other storytelling app does this. Record 30 seconds of your voice — Dreamy Bedtime clones it instantly using smart technology. Every bedtime story your child hears will be in <strong>Mum's or Dad's voice</strong>, even when you're travelling or after bedtime.
            </p>
            <div className="d-flex flex-column gap-3 mb-4">
              {[
                ['🎙️', 'Record once — 30 seconds is all it takes'],
                ['🤖', 'Smart technology clones your voice instantly'],
                ['🌙', 'Every story narrated in your voice, forever'],
                ['💛', 'Massive emotional impact — kids ask for it every night'],
                ['🔒', 'Your recording is private and never shared'],
              ].map(([icon, text]) => (
                <div key={text} className="d-flex align-items-start gap-3">
                  <span style={{fontSize:'18px', lineHeight:1}}>{icon}</span>
                  <span style={{color:'var(--text-muted2)', fontSize:'14px', lineHeight:1.6}}>{text}</span>
                </div>
              ))}
            </div>
            <div className="d-flex flex-wrap gap-3">
              <Link to="/register" className="btn btn-lg rounded-pill px-5 fw-bold"
                style={{background:'linear-gradient(135deg,#ea580c,#f97316)', color:'white', border:'none'}}>
                Try It on Family Plan
              </Link>
              <Link to="/pricing" className="btn btn-lg btn-outline-secondary rounded-pill px-4">
                See Pricing
              </Link>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="rounded-4 p-4" style={{background:'linear-gradient(135deg,#fff7ed,#fed7aa)', border:'2px solid #fdba74'}}>
              {/* Waveform visual */}
              <div className="rounded-3 mb-4 p-4 text-center" style={{background:'var(--card-bg)', boxShadow:'0 4px 20px rgba(234,88,12,0.1)'}}>
                <div style={{fontSize:'13px', color:'#ea580c', fontWeight:600, marginBottom:'12px', textTransform:'uppercase', letterSpacing:'1px'}}>
                  <span className="me-2" style={{display:'inline-block', width:'8px', height:'8px', borderRadius:'50%', background:'#ef4444', animation:'pulse 1s infinite'}}></span>
                  Recording your voice…
                </div>
                {/* Fake waveform bars */}
                <div className="d-flex align-items-center justify-content-center gap-1 mb-3" style={{height:'60px'}}>
                  {[30,55,40,70,50,80,45,65,35,75,50,40,60,30,70,55,45,65,35,50].map((h, i) => (
                    <div key={i} style={{
                      width:'6px', height:`${h}%`, borderRadius:'3px',
                      background:`rgba(234,88,12,${0.4 + (h/100)*0.6})`,
                      alignSelf:'center',
                    }}></div>
                  ))}
                </div>
                <div style={{fontSize:'32px', fontWeight:700, color:'#ea580c', fontFamily:"'Fredoka One',cursive"}}>24s</div>
                <div style={{fontSize:'12px', color:'var(--text-soft)'}}>Recording in progress…</div>
              </div>

              {/* Preview of the result */}
              <div className="rounded-3 p-3 d-flex align-items-center gap-3" style={{background:'var(--card-bg)'}}>
                <div className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{width:'48px', height:'48px', background:'linear-gradient(135deg,#ea580c,#f97316)', flexShrink:0, fontSize:'22px'}}>
                  🎙️
                </div>
                <div className="flex-grow-1">
                  <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'14px'}}>Mum's Voice</div>
                  <div style={{color:'var(--text-soft)', fontSize:'12px'}}>Ready to use · Created in 12 seconds</div>
                </div>
                <span className="badge rounded-pill" style={{background:'#16a34a', color:'white', fontSize:'11px'}}>✓ Active</span>
              </div>

              <div className="mt-3 p-3 rounded-3 d-flex align-items-center gap-3" style={{background:'rgba(234,88,12,0.08)', border:'1px solid #fed7aa'}}>
                <span style={{fontSize:'24px'}}>🌙</span>
                <div>
                  <div style={{fontSize:'13px', fontWeight:600, color:'#9a3412'}}>Story Reader</div>
                  <div style={{fontSize:'12px', color:'#c2410c'}}>
                    <span className="me-2">🎙️ Smart Narration</span>
                    <span style={{background:'linear-gradient(135deg,#ea580c,#f97316)', color:'white', padding:'2px 8px', borderRadius:'50px', fontSize:'11px', fontWeight:600}}>🎙️ Mum's Voice</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── GIFT A STORY ─────────────────────────────────────── */}
    {flags.giftStory !== false && <section className="py-5 bg-white">
      <div className="container py-3">
        <div className="row align-items-center g-5">
          <div className="col-lg-6">
            <span className="section-badge">🎁 New Feature</span>
            <h2 className="fw-bold mb-3" style={{fontFamily:"'Fredoka One',cursive",color:'var(--text-main)',fontSize:'clamp(26px,4vw,38px)'}}>
              Gift a story to any child
            </h2>
            <p style={{color:'var(--text-muted2)',fontSize:'16px',lineHeight:1.8,marginBottom:'24px'}}>
              Perfect for grandparents, aunts, uncles, and friends. Send a fully personalised bedtime story as a one-time gift — delivered by email, no account needed.
            </p>
            <div className="d-flex flex-column gap-3 mb-4">
              {[
                ['✨','Personalised with the child\'s name, age & interests'],
                ['📧','Delivered straight to the parent\'s inbox'],
                ['⚡','Story generated and sent in under a minute'],
                ['💳',`One-time ${pricing.formatted.gift} — no subscription or account needed`],
                ['💌','Add a personal message that appears before the story'],
              ].map(([icon,text]) => (
                <div key={text} className="d-flex align-items-start gap-3">
                  <span style={{fontSize:'18px'}}>{icon}</span>
                  <span style={{color:'var(--text-muted2)',fontSize:'14px',lineHeight:1.6}}>{text}</span>
                </div>
              ))}
            </div>
            <Link to="/gift" className="btn btn-lg rounded-pill px-5 fw-bold" style={{background:'linear-gradient(135deg,#6f42c1,#9b69e8)',color:'white'}}>
              🎁 Gift a Story — {pricing.formatted.gift}
            </Link>
          </div>
          <div className="col-lg-6">
            <div className="card border-0 shadow-lg p-4" style={{borderRadius:'20px',background:'linear-gradient(135deg,#f0e8ff,#e8d5ff)'}}>
              <div className="d-flex align-items-center gap-3 mb-3 p-3 rounded-3" style={{background:'var(--card-bg)'}}>
                <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                  style={{width:'44px',height:'44px',background:'#6f42c1',flexShrink:0}}>G</div>
                <div>
                  <div className="fw-bold" style={{color:'var(--text-main)'}}>Grandma Sarah</div>
                  <div style={{color:'var(--text-soft)',fontSize:'12px'}}>→ parent@family.com</div>
                </div>
                <span className="badge rounded-pill ms-auto" style={{background:'#d4edda',color:'#155724',fontSize:'11px'}}>✅ Delivered</span>
              </div>
              <div className="card border-0 shadow-sm p-4" style={{borderRadius:'16px',background:'var(--card-bg)'}}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span style={{fontSize:'20px'}}>🎁</span>
                  <span className="fw-bold small" style={{color:'#6f42c1'}}>You've received a gift story!</span>
                </div>
                <div className="fw-bold mb-2" style={{color:'var(--text-main)',fontFamily:"'Fredoka One',cursive"}}>Emma and the Moonbeam Dragon</div>
                <p className="fst-italic small mb-2" style={{color:'var(--text-soft)',borderLeft:'3px solid #c9a8ff',paddingLeft:'10px'}}>
                  "Happy birthday sweetheart! Love you to the moon and back. — Grandma 🌙"
                </p>
                <p className="text-muted small mb-0">Once upon a time, a brave girl named Emma discovered a tiny dragon who could make moonbeams dance...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    }

    {/* ── TESTIMONIALS ─────────────────────────────────────── */}
    <section className="py-5" style={{background:'#f8f5ff'}}>
      <div className="container py-3">
        <div className="text-center mb-5">
          <span className="section-badge">Testimonials</span>
          <h2 className="fw-bold" style={{fontFamily:"'Fredoka One',cursive",color:'var(--text-main)',fontSize:'clamp(28px,4vw,40px)'}}>
            Parents love it ❤️
          </h2>
        </div>
        <div className="row g-4">
          {testimonials.map(t => (
            <div className="col-md-6 col-lg-4" key={t.name}>
              <div className="card border-0 shadow-sm h-100 p-4" style={{borderRadius:'18px',background:'var(--card-bg)'}}>
                <div className="mb-3" style={{color:'#f9c74f',fontSize:'16px',letterSpacing:'2px'}}>★★★★★</div>
                <p style={{color:'var(--text-body)',fontSize:'14px',lineHeight:1.8,marginBottom:'20px'}}>"{t.quote}"</p>
                <div className="d-flex align-items-center gap-3 mt-auto">
                  <div className="rounded-circle text-white fw-bold d-flex align-items-center justify-content-center"
                    style={{width:'44px',height:'44px',background:t.color,flexShrink:0,fontSize:'16px'}}>
                    {t.avatar}
                  </div>
                  <div>
                    <div className="fw-bold" style={{color:'var(--text-main)',fontSize:'14px'}}>{t.name}</div>
                    <div style={{color:'var(--text-soft)',fontSize:'12px'}}>{t.role}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── PRICING PREVIEW ──────────────────────────────────── */}
    <section className="py-5 bg-white">
      <div className="container py-3">
        <div className="text-center mb-5">
          <span className="section-badge">Pricing</span>
          <h2 className="fw-bold" style={{fontFamily:"'Fredoka One',cursive",color:'var(--text-main)',fontSize:'clamp(28px,4vw,40px)'}}>
            Simple, honest pricing
          </h2>
          <p className="text-muted" style={{fontSize:'16px'}}>Start free forever. Upgrade when you're ready.</p>
        </div>
        <div className="row g-4 justify-content-center mb-4">
          {[
            {
              name: 'Free', price: `${pricing.symbol}0`, period: 'forever',
              color: 'var(--text-muted2)', bg: '#f8f9fa', popular: false, outline: true,
              cta: 'Start Free', link: '/register',
              features: [
                '5 stories / month',
                '1 child profile',
                'Story library — saved forever',
                'Magical illustrations',
                'All 11 languages',
                'Bedtime reminders',
                'Reading reports',
              ],
            },
            {
              name: 'Basic', price: pricing.formatted.basic, period: '/ month',
              color: '#6f42c1', bg: 'var(--page-bg)', popular: false, outline: false,
              cta: 'Get Basic', link: '/pricing',
              features: [
                '60 stories / month',
                'Up to 3 child profiles',
                'AI narration — 30 / month',
                '📸 Photo illustrations — 3 / month',
                '20 illustration regenerations / month',
                'Story Series — multi-chapter adventures',
                'Story Quiz · PDF export · All 11 languages',
              ],
            },
            {
              name: 'Family', price: pricing.formatted.family, period: '/ month',
              color: '#fd7e14', bg: '#fff8f0', popular: true, outline: false,
              cta: 'Get Family', link: '/pricing',
              features: [
                '120 stories / month',
                'Unlimited child profiles',
                '🎙️ Voice cloning — narrate in your own voice',
                'AI narration — 40 / month',
                '📸 Photo illustrations — 6 / month',
                '40 illustration regenerations / month',
                'Grandparent sharing · Priority support',
              ],
            },
          ].map(p => (
            <div className="col-md-4" key={p.name}>
              <div className="card border-0 shadow-sm h-100 p-4" style={{borderRadius:'20px', background:p.bg, border: p.popular ? `2px solid ${p.color}` : '1px solid #eee'}}>
                {p.popular && (
                  <div className="text-center mb-3">
                    <span className="badge rounded-pill px-3 py-2" style={{background:p.color, color:'white', fontSize:'12px'}}>⭐ Most Popular</span>
                  </div>
                )}
                <div className="mb-3">
                  <div className="fw-bold" style={{color:p.color, fontFamily:"'Fredoka One',cursive", fontSize:'22px'}}>{p.name}</div>
                  <div className="d-flex align-items-baseline gap-1">
                    <span className="fw-bold" style={{fontSize:'36px', color:'var(--text-main)', fontFamily:"'Fredoka One',cursive"}}>{p.price}</span>
                    <span style={{color:'var(--text-soft)', fontSize:'14px'}}>{p.period}</span>
                  </div>
                </div>
                <ul className="list-unstyled mb-4 flex-grow-1">
                  {p.features.map(f => (
                    <li key={f} className="d-flex align-items-start gap-2 mb-2" style={{fontSize:'13px', color:'var(--text-body)'}}>
                      <i className="bi bi-check-circle-fill mt-1 flex-shrink-0" style={{color:'#4ade80'}}></i>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={p.link} className={`btn rounded-pill w-100 fw-semibold ${p.outline ? 'btn-outline-secondary' : 'btn-dream'}`}
                  style={p.popular ? {background:'linear-gradient(135deg,#fd7e14,#e65c00)', border:'none', color:'#fff'} : {}}>
                  {p.cta}
                </Link>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center">
          <Link to="/pricing" style={{color:'#6f42c1', fontWeight:600}}>
            See full feature comparison & annual pricing <i className="bi bi-arrow-right ms-1"></i>
          </Link>
        </div>
      </div>
    </section>

    {/* ── FINAL CTA ─────────────────────────────────────────── */}
    <section className="bg-dream text-white py-5">
      <div className="container py-4 text-center">
        <div style={{fontSize:'52px',marginBottom:'16px'}} className="float-anim">🌙</div>
        <h2 className="fw-bold mb-3" style={{fontFamily:"'Fredoka One',cursive",fontSize:'clamp(28px,5vw,44px)'}}>
          Be the storyteller your child thinks you already are
        </h2>
        <p className="mb-4" style={{color:'#c9a8ff',fontSize:'18px',maxWidth:'560px',margin:'0 auto 32px'}}>
          Join thousands of families making bedtime the most magical part of the day.
        </p>
        <div className="d-flex flex-wrap gap-3 justify-content-center">
          <Link to="/register" className="btn btn-warning btn-lg px-5 rounded-pill fw-bold" style={{fontSize:'17px'}} onClick={() => trackLandingCta('Start Free Today', '/register')}>
            Start Free Today — No Card Needed
          </Link>
          {flags.giftStory !== false && (
            <Link to="/gift" className="btn btn-outline-light btn-lg px-4 rounded-pill" onClick={() => trackLandingCta('Gift a Story', '/gift')}>
              🎁 Gift a Story
            </Link>
          )}
        </div>
        <p className="mt-3" style={{color:'rgba(255,255,255,0.5)',fontSize:'13px'}}>
          5 free stories every month, forever. No credit card required.
        </p>
        <p className="mt-4 mb-2" style={{color:'#c9a8ff',fontSize:'14px'}}>Or get the app on your phone</p>
        <AppStoreBadges placement="final_cta" height={44} justify="center" />
      </div>
    </section>

    {/* ── FOOTER ───────────────────────────────────────────── */}
    <footer style={{background:'#1a0533',color:'#c9a8ff',padding:'48px 0 32px'}}>
      <div className="container">
        <div className="row g-4 mb-4">
          <div className="col-md-4">
            <div style={{fontFamily:"'Fredoka One',cursive",fontSize:'22px',color:'#fff',marginBottom:'8px'}}>🌙 Dreamy Bedtime</div>
            <p style={{color:'#9b7dd4',fontSize:'14px',lineHeight:1.7,marginBottom:'16px'}}>
              Magical personalised bedtime stories for every child. Unique, educational, and loved by kids worldwide.
            </p>
            <div className="d-flex gap-2">
              {['⭐ 4.9 Rating','👨‍👩‍👧 Family Friendly','🔒 Privacy First'].map(t => (
                <span key={t} className="badge rounded-pill" style={{background:'rgba(255,255,255,0.08)',color:'#c9a8ff',fontSize:'11px',padding:'5px 10px'}}>{t}</span>
              ))}
            </div>
            <AppStoreBadges placement="footer" height={38} className="mt-3" />
          </div>
          <div className="col-md-2">
            <div style={{color:'#fff',fontWeight:700,marginBottom:'12px',fontSize:'14px'}}>Product</div>
            {[['/',  'Home'],['/pricing','Pricing'],flags.giftStory !== false && ['/gift','Gift a Story'],['/register','Get Started']].filter(Boolean).map(([to,label]) => (
              <div key={label} className="mb-2">
                <Link to={to} style={{color:'#9b7dd4',textDecoration:'none',fontSize:'14px'}} className="hover-white">{label}</Link>
              </div>
            ))}
          </div>
          <div className="col-md-2">
            <div style={{color:'#fff',fontWeight:700,marginBottom:'12px',fontSize:'14px'}}>Account</div>
            {[['/login','Log In'],['/register','Sign Up'],['/dashboard','Dashboard'],['/dashboard/settings','Settings']].map(([to,label]) => (
              <div key={label} className="mb-2">
                <Link to={to} style={{color:'#9b7dd4',textDecoration:'none',fontSize:'14px'}}>{label}</Link>
              </div>
            ))}
          </div>
          <div className="col-md-2">
            <div style={{color:'#fff',fontWeight:700,marginBottom:'12px',fontSize:'14px'}}>Company</div>
            {[['/about','About Us'],['/faq','FAQ'],['/blog','Blog'],['/affiliate','Affiliates'],['/contact','Contact']].map(([to,label]) => (
              <div key={label} className="mb-2">
                <Link to={to} style={{color:'#9b7dd4',textDecoration:'none',fontSize:'14px'}}>{label}</Link>
              </div>
            ))}
          </div>
          <div className="col-md-2">
            <div style={{color:'#fff',fontWeight:700,marginBottom:'12px',fontSize:'14px'}}>Why Dreamy Bedtime?</div>
            {[
              '🌙 Stories ready in under 15 seconds',
              '🎨 Custom illustration per story',
              '🎙️ Voice cloning — stories in your voice',
              '🌍 10+ languages supported',
              '🔔 Daily bedtime reminders',
            ].map(t => (
              <div key={t} style={{color:'#9b7dd4',fontSize:'13px',marginBottom:'6px'}}>{t}</div>
            ))}
          </div>
        </div>
        {/* Explore hubs — funnels homepage authority into the SEO landing hubs
            (helps discovery + ranking of the /bedtime-stories/* pages) */}
        <div style={{borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:'24px',marginBottom:'8px'}}>
          <div style={{color:'#fff',fontWeight:700,marginBottom:'12px',fontSize:'14px'}}>Explore Bedtime Stories</div>
          <div className="d-flex flex-wrap gap-2">
            {[
              ['/bedtime-stories/for',       'Stories by Name'],
              ['/bedtime-stories/themes',    'Story Themes'],
              ['/bedtime-stories/languages', 'Stories by Language'],
              ['/bedtime-stories/animals',   'Animal Stories'],
              ['/bedtime-stories/settings',  'Magical Settings'],
              ['/bedtime-stories/mood',      'Stories by Mood'],
              ['/bedtime-stories/free',      'Free Bedtime Stories'],
            ].map(([to, label]) => (
              <Link key={to} to={to}
                style={{color:'#c9a8ff',textDecoration:'none',fontSize:'13px',background:'rgba(255,255,255,0.06)',padding:'6px 14px',borderRadius:'50px'}}>
                {label}
              </Link>
            ))}
          </div>
        </div>
        <div style={{borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:'24px',display:'flex',flexWrap:'wrap',justifyContent:'space-between',alignItems:'center',gap:'12px'}}>
          <span style={{color:'#6b5a8a',fontSize:'13px'}}>© 2026 Prajapati LLC · All rights reserved</span>
          <div className="d-flex gap-3 flex-wrap">
            <a href="mailto:support@dreamybedtime.com" style={{color:'#9b7dd4',textDecoration:'none',fontSize:'13px'}}>support@dreamybedtime.com</a>
            <Link to="/contact" style={{color:'#9b7dd4',textDecoration:'none',fontSize:'13px'}}>Contact Us</Link>
            <Link to="/terms" style={{color:'#9b7dd4',textDecoration:'none',fontSize:'13px'}}>Terms</Link>
            <Link to="/privacy" style={{color:'#9b7dd4',textDecoration:'none',fontSize:'13px'}}>Privacy</Link>
            <Link to="/cookies" style={{color:'#9b7dd4',textDecoration:'none',fontSize:'13px'}}>Cookies</Link>
            <Link to="/faq" style={{color:'#9b7dd4',textDecoration:'none',fontSize:'13px'}}>FAQ</Link>
          </div>
        </div>
      </div>
    </footer>
  </div>
  );
};

export default LandingPage;
